import { useMemo, useState } from 'react';
import LinkIcon from '@mui/icons-material/Link';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {
    ListBase,
    ListToolbar,
    Pagination,
    Title,
    useGetList,
    useListContext,
    useNotify,
    useRefresh,
} from 'react-admin';

import {
    type ExpenseTransaction,
    type Receipt,
    type ReceiptCategory,
    type ResponsibleUser,
    matchMyReceipt,
    postMyTransaction,
    unmatchMyReceipt,
} from './receiptApi';
import {
    AllocationFields,
    allocationChanges,
    allocationFrom,
    allocationReady,
    type Allocation,
} from './AllocationFields';
import { ReceiptAllocationEditDialog } from './ReceiptAllocationEditDialog';
import { formatDate } from '@/utils/date_utils';
import {
    statusColor,
    allocationColor,
    type QuickRange,
    quickDateRange,
} from './receiptHelpers';
import { receiptDateRangeFilters } from './ReceiptDateRangeFilters';
import { ReceiptMatchDialog } from './ReceiptMatchDialog';
import { ReceiptPreviewDialog } from './ReceiptPreviewDialog';
import {
    FilterSection,
    QUICK_RANGES,
    type ActiveFilterChip,
    ReceiptFiltersAccordion,
    QuickRangeButtons,
    quickRangeLabel,
    ClearFiltersButton,
    type QuickRangeOption,
} from './ReceiptFilterParts';

type Props = {
    users: ResponsibleUser[];
    categories: ReceiptCategory[];
    /** Coworker being helped; null/undefined = the current user's own charges. */
    ownerId?: number | null;
};

const ownerFilter = (ownerId?: number | null) =>
    ownerId ? { responsible_user_id: ownerId } : { mine: true };

export const MyExpensesList = ({ users, categories, ownerId }: Props) => (
    <ListBase
        resource="expense_transactions"
        storeKey="receipts.my_expenses"
        disableAuthentication
        filter={ownerFilter(ownerId)}
        perPage={25}
        sort={{ field: 'transaction_date', order: 'DESC' }}
    >
        <MyExpensesLayout users={users} categories={categories} ownerId={ownerId} />
    </ListBase>
);

const MyExpensesLayout = ({ users, categories, ownerId }: Props) => {
    const { data, isPending, filterValues } = useListContext<ExpenseTransaction>();
    const stickyKeys = ['mine'];
    const hasFilters = Object.keys(filterValues || {}).some(
        key => !stickyKeys.includes(key)
    );

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Title title={ownerId ? 'Expenses' : 'My expenses'} />
            <MyExpensesFilters />
            {!isPending && !data?.length && !hasFilters ? (
                <Alert severity="info">
                    {ownerId ? 'No card expenses are assigned to this person yet.' : 'No card expenses are assigned to you yet.'}
                </Alert>
            ) : !isPending && !data?.length ? (
                <Alert severity="info">No expenses found for these filters.</Alert>
            ) : (
                <>
                    <Card>
                        <MyExpensesContent users={users} categories={categories} ownerId={ownerId} />
                    </Card>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
        </Stack>
    );
};


const buildActiveFilterChips = (
    filterValues: Record<string, unknown> | undefined,
    quickRangeOptions: QuickRangeOption[]
): ActiveFilterChip[] => {
    const chips: ActiveFilterChip[] = [];

    if (filterValues?.has_receipt === true) {
        chips.push({ id: 'match', label: 'Matched', color: 'success' });
    } else if (filterValues?.has_receipt === false) {
        chips.push({ id: 'match', label: 'Unmatched', color: 'warning' });
    }

    const dateLabel = quickRangeLabel(filterValues || {}, quickRangeOptions);
    if (dateLabel) {
        chips.push({
            id: 'date',
            label: dateLabel,
            color: 'info',
        });
    }

    if (filterValues?.allocation_status === 'needs_correction') {
        chips.push({ id: 'needs_correction', label: 'Needs correction', color: 'error' });
    }

    return chips;
};

const MyExpensesFilters = () => {
    const { filterValues, setFilters } = useListContext<ExpenseTransaction>();
    const currentHasReceipt = filterValues?.has_receipt;
    const matchStatus = currentHasReceipt === true ? 'matched' : currentHasReceipt === false ? 'unmatched' : 'all';

    const quickRangeOptions = useMemo(
        () =>
            QUICK_RANGES.map(({ range, label }) => ({
                key: range,
                label,
                value: quickDateRange(range),
            })),
        []
    );

    const activeChips = useMemo(
        () => buildActiveFilterChips(filterValues, quickRangeOptions),
        [filterValues, quickRangeOptions]
    );

    const handleMatchStatusChange = (
        _event: React.MouseEvent<HTMLElement>,
        newValue: string | null
    ) => {
        if (newValue === null) return;

        if (newValue === 'all') {
            const next = { ...filterValues };
            delete next.has_receipt;
            setFilters(next, undefined, false);
        } else if (newValue === 'matched') {
            setFilters(
                { ...filterValues, has_receipt: true },
                undefined,
                false
            );
        } else if (newValue === 'unmatched') {
            setFilters(
                { ...filterValues, has_receipt: false },
                undefined,
                false
            );
        }
    };

    const handleQuickRange = (value: { transaction_date_gte: string; transaction_date_lte: string }) => {
        setFilters(
            { ...filterValues, ...value },
            undefined,
            false
        );
    };

    const isNeedsCorrectionActive =
        filterValues?.allocation_status === 'needs_correction';

    const handleNeedsCorrectionToggle = () => {
        if (isNeedsCorrectionActive) {
            const newFilters = { ...filterValues };
            delete newFilters.allocation_status;
            setFilters(newFilters, undefined, false);
        } else {
            setFilters(
                { ...filterValues, allocation_status: 'needs_correction' },
                undefined,
                false
            );
        }
    };

    return (
        <ReceiptFiltersAccordion
            idPrefix="my-expenses"
            chips={activeChips}
            clearButton={<MyExpensesClearFilters />}
        >
            <FilterSection
                title="Receipt match"
                legend="Show expenses by whether a receipt upload is already linked to the card charge."
            >
                <ToggleButtonGroup
                    size="small"
                    value={matchStatus}
                    exclusive
                    onChange={handleMatchStatusChange}
                    aria-label="Receipt match status"
                >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="unmatched">Unmatched</ToggleButton>
                    <ToggleButton value="matched">Matched</ToggleButton>
                </ToggleButtonGroup>
            </FilterSection>

            <FilterSection
                title="Transaction date"
                legend="Jump to a common period, or set an exact From/To range. Changing From/To replaces a quick period."
            >
                <Stack spacing={1.5}>
                    <QuickRangeButtons
                        ranges={quickRangeOptions}
                        filterValues={filterValues || {}}
                        onSelect={handleQuickRange}
                    />
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: 0.5 }}
                        >
                            Custom range
                        </Typography>
                        <ListToolbar
                            filters={receiptDateRangeFilters}
                            actions={false}
                        />
                    </Box>
                </Stack>
            </FilterSection>

            <FilterSection
                title="Allocation review"
                legend="Limit the list to expenses whose linked receipt needs a correction."
            >
                <Chip
                    label="Needs correction"
                    onClick={handleNeedsCorrectionToggle}
                    color={isNeedsCorrectionActive ? 'error' : 'default'}
                    variant={isNeedsCorrectionActive ? 'filled' : 'outlined'}
                    clickable
                />
            </FilterSection>
        </ReceiptFiltersAccordion>
    );
};

const MyExpensesClearFilters = () => (
    <ClearFiltersButton stickyKeys={['mine']} />
);

const MyExpensesContent = ({ users, categories, ownerId }: Props) => {
    const { data = [], isPending } = useListContext<ExpenseTransaction>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [matchingTransaction, setMatchingTransaction] =
        useState<ExpenseTransaction | null>(null);
    const [matchingId, setMatchingId] = useState<number | null>(null);
    const [previewingId, setPreviewingId] = useState<number | null>(null);
    const [editingReceipt, setEditingReceipt] = useState<Receipt | null>(null);

    const { data: linkedReceipts = [] } = useGetList<Receipt>('receipts', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'DESC' },
        filter: { ids: data.filter(tx => tx.receipt_id).map(tx => tx.receipt_id!) },
    });

    const receiptMap = useMemo(
        () => new Map(linkedReceipts.map(r => [r.id, r])),
        [linkedReceipts]
    );

    const userName = useMemo(() => {
        const byId = new Map(
            users.map(user => [user.id, user.name || user.email] as const)
        );
        return (id: number | null) => (id == null ? undefined : byId.get(id));
    }, [users]);

    const handleMatch = async (
        transactionId: number,
        receiptId: number
    ): Promise<boolean> => {
        setMatchingId(transactionId);
        try {
            await matchMyReceipt(transactionId, receiptId);
            notify('Receipt matched successfully', { type: 'success' });
            refresh();
            return true;
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Match failed',
                { type: 'error' }
            );
            return false;
        } finally {
            setMatchingId(null);
        }
    };

    const handleUnlink = async (transactionId: number) => {
        setMatchingId(transactionId);
        try {
            await unmatchMyReceipt(transactionId);
            notify('Receipt unlinked successfully', { type: 'success' });
            refresh();
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Unlink failed',
                { type: 'error' }
            );
        } finally {
            setMatchingId(null);
        }
    };

    if (isPending) return null;

    return (
        <>
            <Stack spacing={1}>
                {data.map(transaction => {
                    const linkedReceipt =
                        transaction.receipt_id != null
                            ? receiptMap.get(transaction.receipt_id)
                            : undefined;
                    return (
                    <Card key={transaction.id} variant="outlined">
                        <CardContent>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ md: 'center' }}
                                spacing={2}
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <Box
                                        sx={{
                                            minWidth: 74,
                                            pr: 2,
                                            borderRight: 1,
                                            borderColor: 'divider',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {new Date(
                                                `${transaction.transaction_date}T00:00:00`
                                            )
                                                .toLocaleDateString(
                                                    undefined,
                                                    { month: 'short' }
                                                )
                                                .toUpperCase()}
                                        </Typography>
                                        <Typography
                                            variant="h5"
                                            lineHeight={1}
                                        >
                                            {Number(
                                                transaction.transaction_date.slice(
                                                    -2
                                                )
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {transaction.transaction_date.slice(
                                                0,
                                                4
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography fontWeight={600}>
                                            {transaction.description} · $
                                            {transaction.amount}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {transaction.source_network}
                                            {transaction.card_last4
                                                ? ` ••••${transaction.card_last4}`
                                                : ''}
                                            {' · '}
                                            {transaction.transaction_date}
                                        </Typography>
                                        {transaction.posted_by != null &&
                                            transaction.posted_by !== transaction.responsible_user_id && (
                                                <Typography variant="body2" color="info.main">
                                                    Posted by {userName(transaction.posted_by) ?? 'a coworker'}
                                                </Typography>
                                            )}
                                        {linkedReceipt?.created_by != null &&
                                            linkedReceipt.created_by !== transaction.responsible_user_id && (
                                                <Typography variant="body2" color="info.main">
                                                    Receipt uploaded by {userName(linkedReceipt.created_by) ?? 'a coworker'}
                                                </Typography>
                                            )}
                                        {transaction.discrepancy && (
                                            <Typography
                                                variant="body2"
                                                color="warning.main"
                                            >
                                                {transaction.discrepancy}
                                            </Typography>
                                        )}
                                        {linkedReceipt &&
                                            linkedReceipt.allocation_status !==
                                                'pending' && (
                                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                <Chip
                                                    size="small"
                                                    label={`allocation ${linkedReceipt.allocation_status.replaceAll('_', ' ')}`}
                                                    color={allocationColor(
                                                        linkedReceipt.allocation_status
                                                    )}
                                                />
                                                {linkedReceipt.allocation_review_notes && (
                                                    <Typography
                                                        variant="body2"
                                                        color="error.main"
                                                    >
                                                        {linkedReceipt.allocation_status ===
                                                        'needs_correction'
                                                            ? 'Bookkeeper correction requested: '
                                                            : 'Review notes: '}
                                                        {
                                                            linkedReceipt.allocation_review_notes
                                                        }
                                                        {linkedReceipt.allocation_reviewed_at && (
                                                            <>
                                                                {' · '}
                                                                {formatDate(
                                                                    linkedReceipt.allocation_reviewed_at,
                                                                    'MM-dd-yyyy'
                                                                )}
                                                            </>
                                                        )}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    sx={{ flexWrap: 'wrap' }}
                                >
                                    <Chip
                                        size="small"
                                        label={transaction.status.replaceAll(
                                            '_',
                                            ' '
                                        )}
                                        color={statusColor(transaction.status)}
                                    />
                                    <PostControls
                                        transaction={transaction}
                                        receipt={linkedReceipt}
                                        categories={categories}
                                        onEditReceipt={setEditingReceipt}
                                    />

                                    {transaction.receipt_id == null ? (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<LinkIcon />}
                                            disabled={
                                                matchingId === transaction.id
                                            }
                                            onClick={() =>
                                                setMatchingTransaction(
                                                    transaction
                                                )
                                            }
                                            sx={{ minHeight: '44px' }}
                                        >
                                            Match
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    setPreviewingId(
                                                        transaction.receipt_id
                                                    )
                                                }
                                            >
                                                View receipt
                                            </Button>
                                            {linkedReceipt?.allocation_status !==
                                                'verified' && (
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    disabled={
                                                        matchingId ===
                                                        transaction.id
                                                    }
                                                    onClick={() =>
                                                        void handleUnlink(
                                                            transaction.id
                                                        )
                                                    }
                                                >
                                                    Unlink
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                    );
                })}
            </Stack>

            {matchingTransaction && (
                <ReceiptMatchDialog
                    transaction={matchingTransaction}
                    users={users}
                    busy={matchingId === matchingTransaction.id}
                    receiptFilter={ownerFilter(ownerId)}
                    onClose={() => setMatchingTransaction(null)}
                    onMatch={handleMatch}
                />
            )}

            <ReceiptAllocationEditDialog
                receipt={editingReceipt}
                categories={categories}
                users={users}
                onClose={() => setEditingReceipt(null)}
                onSaved={refresh}
            />

            <ReceiptPreviewDialog
                receipt={previewingId ? (receiptMap.get(previewingId) || null) : null}
                onClose={() => setPreviewingId(null)}
            />
        </>
    );
};

const receiptDone = (receipt: Receipt | undefined) =>
    receipt?.allocation_status === 'verified' && !!receipt.qbo_pushed_at && !receipt.qbo_push_error;

/** Employee side: the employee only posts to the admin; the admin is the one who sends to QuickBooks. */
const PostControls = ({
    transaction,
    receipt,
    categories,
    onEditReceipt,
}: {
    transaction: ExpenseTransaction;
    receipt: Receipt | undefined;
    categories: ReceiptCategory[];
    onEditReceipt: (receipt: Receipt) => void;
}) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [note, setNote] = useState('');
    const [allocation, setAllocation] = useState<Allocation>(allocationFrom({}));
    const withReceipt = transaction.receipt_id != null;

    const approved = !!transaction.qbo_pushed_at || receiptDone(receipt);
    const correction =
        !approved &&
        !transaction.posted_at &&
        (!!transaction.review_note || receipt?.allocation_status === 'needs_correction');
    const [label, color] = approved
        ? (['Approved (in QuickBooks)', 'success'] as const)
        : transaction.posted_at
          ? (['Posted · waiting for admin', 'info'] as const)
          : correction
            ? (['Needs correction', 'error'] as const)
            : (['Not posted', 'default'] as const);

    const post = async () => {
        setSaving(true);
        try {
            await postMyTransaction(
                transaction.id,
                note.trim(),
                withReceipt ? undefined : allocationChanges(allocation)
            );
            notify('Posted to the admin', { type: 'success' });
            setOpen(false);
            refresh();
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Post failed', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Chip size="small" color={color} variant="outlined" label={label} />
            {!approved && withReceipt && receipt && (
                <Button size="small" variant="outlined" onClick={() => onEditReceipt(receipt)}>
                    Project &amp; Expense
                </Button>
            )}
            {!approved && !transaction.posted_at && (
                <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={withReceipt && !receipt}
                    onClick={() => {
                        setNote(transaction.post_note || '');
                        setAllocation(allocationFrom(transaction));
                        setOpen(true);
                    }}
                    sx={{ minHeight: '44px' }}
                >
                    {withReceipt ? 'Post' : 'Post without receipt'}
                </Button>
            )}
            {transaction.review_note && !transaction.posted_at && !approved && (
                <Typography variant="body2" color="error.main" sx={{ width: '100%', textAlign: 'right' }}>
                    Admin: {transaction.review_note}
                </Typography>
            )}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{withReceipt ? 'Post to admin' : 'Post without receipt'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {transaction.description} · ${transaction.amount}.{' '}
                        {withReceipt
                            ? `${receipt?.allocation_type === 'project_cost' ? `Project #${receipt.project_id}` : receipt?.category || 'No category'}${receipt?.subcategory ? ` · ${receipt.subcategory}` : ''}. The admin reviews it before it goes to QuickBooks.`
                            : 'Pick the project or category and explain why there is no receipt. The admin reviews it before it goes to QuickBooks.'}
                    </Typography>
                    <Stack spacing={2}>
                        {!withReceipt && (
                            <AllocationFields
                                value={allocation}
                                onChange={setAllocation}
                                categories={categories}
                                showClassNote
                            />
                        )}
                        <TextField
                            multiline
                            minRows={2}
                            required={!withReceipt}
                            label={withReceipt ? 'Note for the admin (optional)' : 'Why is there no receipt?'}
                            value={note}
                            onChange={event => setNote(event.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={
                            saving ||
                            (!withReceipt && (!note.trim() || !allocationReady(allocation, categories)))
                        }
                        onClick={() => void post()}
                    >
                        Post
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
